import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  } from 'react-native';
import { randomUUID } from '../../../src/utils/uuid';
import { useRouter } from 'expo-router';
import KeyboardAwareView from '../../../src/components/KeyboardAwareView';
import DatePickerModal from '../../../src/components/DatePickerModal';
import AiSuggestionsCard from '../../../src/components/AiSuggestionsCard';
import AiUnavailableBanner from '../../../src/components/AiUnavailableBanner';
import { showAlert } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useOnDeviceLlm } from '../../../src/hooks/useOnDeviceLlm';
import { goalsApi, visionsApi, Vision } from '../../../src/api/client';
import { entityMutation } from '../../../src/api/queryClient';
import type { SuggestionItem } from '../../../src/services/llm';
import { encrypt, encryptTitle, mergeEncryptionMetadata, decryptEntityTitle, getKeyForTeam } from '../../../src/utils/encryption';
import { useTeamStore } from '../../../src/stores/teamStore';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { formatDisplayDateLong } from '../../../src/utils/format';
import { DEFAULT_GOAL_DURATION_DAYS } from '../../../src/constants';
import { Measure, initializeMilestones } from '../../../src/utils/milestones';
import ScreenHeader from '../../../src/components/ScreenHeader';

export default function CreateGoalScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useThemedStyles(createStyles);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedVisionId, setSelectedVisionId] = useState<string | undefined>();
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [endDate, setEndDate] = useState(() => {
    const end = new Date();
    end.setDate(end.getDate() + DEFAULT_GOAL_DURATION_DAYS);
    return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  });
  const [visions, setVisions] = useState<Vision[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVisionPicker, setShowVisionPicker] = useState(false);
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<SuggestionItem[]>([]);
  const [showAiResults, setShowAiResults] = useState(false);
  const [measures, setMeasures] = useState<Array<{
    id: string;
    name: string;
    unit: string;
    start_value: string;
    target_value: string;
  }>>([]);
  const [milestoneCount, setMilestoneCount] = useState<number | null>(null);
  const llm = useOnDeviceLlm();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);

  // Local map of decrypted vision titles for display
  const [visionTitles, setVisionTitles] = useState<Record<string, string>>({});

  useEffect(() => {
    visionsApi.list().then(async (res) => {
      const visionList = res.data;
      setVisions(visionList);
      // Decrypt vision titles for picker display (auto-dispatches via team_id)
      const entries = await Promise.all(
        visionList.map(async (v): Promise<[string, string]> => [v.id, await decryptEntityTitle(v)])
      );
      setVisionTitles(Object.fromEntries(entries));
    }).catch(() => {});
  }, []);

  const addMeasure = useCallback(() => {
    setMeasures((prev) => [
      ...prev,
      { id: randomUUID(), name: '', unit: '', start_value: '', target_value: '' },
    ]);
  }, []);

  const updateMeasure = useCallback((id: string, field: string, value: string) => {
    setMeasures((prev) =>
      prev.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  }, []);

  const removeMeasure = useCallback((id: string) => {
    setMeasures((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const handleAiSuggest = async () => {
    if (!title.trim()) {
      showAlert('Add a Title', 'Enter a goal title first so AI can help refine it.');
      return;
    }
    setShowAiResults(true);
    setAiSuggestions([]);
    const result = await llm.suggest('goal', title.trim());
    if (result) {
      setAiSuggestions(result.suggestions);
    }
  };

  const selectedVision = visions.find((v) => v.id === selectedVisionId);

  const handleSubmit = async () => {
    if (!requireConnectivity()) return;
    if (!title.trim()) {
      showAlert('Required', 'Please enter a goal title.');
      return;
    }

    setIsSubmitting(true);
    try {
      const key = await getKeyForTeam(activeTeamId);
      const { title_encrypted, title_iv } = await encryptTitle(title.trim(), key);
      let descEncrypted: string | undefined;
      let descIv: string | undefined;
      if (description.trim()) {
        const descResult = await encrypt(description.trim(), key);
        descEncrypted = descResult.encrypted;
        descIv = descResult.metadata.iv;
      }
      const startDateResult = await encrypt(startDate, key);
      const endDateResult = await encrypt(endDate, key);

      // Build measures JSON if any valid measures exist
      const validMeasures = measures.filter(
        (m) => m.name.trim() && m.unit.trim() && m.start_value && m.target_value
      );
      let metricsEncrypted: string | undefined;
      let metricsIv: string | undefined;
      const effectiveMilestoneCount = validMeasures.length > 0 ? milestoneCount : null;

      if (validMeasures.length > 0) {
        const measuresData: Measure[] = validMeasures.map((m) => ({
          id: m.id,
          name: m.name.trim(),
          unit: m.unit.trim(),
          start_value: parseFloat(m.start_value),
          target_value: parseFloat(m.target_value),
          milestones: effectiveMilestoneCount
            ? initializeMilestones(effectiveMilestoneCount)
            : [],
        }));
        const metricsResult = await encrypt(JSON.stringify(measuresData), key);
        metricsEncrypted = metricsResult.encrypted;
        metricsIv = metricsResult.metadata.iv;
      }

      await entityMutation(() =>
        goalsApi.create({
          title_encrypted,
          description_encrypted: descEncrypted,
          encryption_metadata: mergeEncryptionMetadata(descIv, title_iv, {
            start_date_iv: startDateResult.metadata.iv,
            end_date_iv: endDateResult.metadata.iv,
            ...(metricsIv ? { metrics_iv: metricsIv } : {}),
          }),
          vision_id: selectedVisionId,
          start_date_encrypted: startDateResult.encrypted,
          end_date_encrypted: endDateResult.encrypted,
          metrics_encrypted: metricsEncrypted,
          milestone_count: effectiveMilestoneCount ?? undefined,
        }),
      );
      router.back();
    } catch {
      showAlert('Error', 'Failed to create goal.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAwareView style={styles.container}>
      <ScreenHeader
        title="New Goal"
        left={{
          label: 'Cancel',
          onPress: () => router.back(),
          testID: 'goal-create.cancel-button',
        }}
        right={{
          label: 'Save goal',
          text: 'Save',
          tone: 'primary',
          onPress: handleSubmit,
          disabled: isSubmitting || !title.trim(),
          loading: isSubmitting,
          testID: 'goal-create.save-button',
        }}
      />
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} keyboardShouldPersistTaps="handled">
        {/* Title */}
        <Text style={styles.label}>TITLE *</Text>
        <TextInput
          style={styles.input}
          value={title}
          onChangeText={setTitle}
          placeholder="What do you want to achieve?"
          placeholderTextColor={colors.placeholder}
          autoFocus
          accessibilityLabel="Goal title"
          testID="goal-create.title-input"
        />

        {/* Linked Vision */}
        <Text style={styles.label}>LINKED VISION</Text>
        <TouchableOpacity
          style={styles.selector}
          onPress={() => setShowVisionPicker(!showVisionPicker)}
          accessibilityLabel={selectedVision ? `Linked vision: ${visionTitles[selectedVision.id] || 'Untitled'}` : 'Select a vision (optional)'}
          accessibilityRole="button"
          testID="goal-create.vision-selector"
        >
          <Text style={styles.selectorText} accessible={false}>
            {selectedVision ? `\u2728 ${visionTitles[selectedVision.id] || 'Untitled'}` : 'Select a vision (optional)'}
          </Text>
          <Text style={styles.chevron} accessible={false}>{showVisionPicker ? '\u25B2' : '\u25BC'}</Text>
        </TouchableOpacity>

        {showVisionPicker && (
          <View style={styles.pickerList}>
            <TouchableOpacity
              style={[styles.pickerItem, !selectedVisionId && styles.pickerItemActive]}
              onPress={() => { setSelectedVisionId(undefined); setShowVisionPicker(false); }}
            >
              <Text style={styles.pickerItemText}>None</Text>
            </TouchableOpacity>
            {visions.map((v) => (
              <TouchableOpacity
                key={v.id}
                style={[styles.pickerItem, selectedVisionId === v.id && styles.pickerItemActive]}
                onPress={() => { setSelectedVisionId(v.id); setShowVisionPicker(false); }}
              >
                <Text style={styles.pickerItemText} numberOfLines={1}>{visionTitles[v.id] || 'Untitled'}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Timeline */}
        <Text style={styles.label}>TIMELINE</Text>
        <View style={styles.dateRow}>
          <View style={styles.dateField}>
            <Text style={styles.dateLabel}>Start Date</Text>
            <TouchableOpacity
              style={styles.dateInput}
              onPress={() => setShowStartPicker(true)}
              accessibilityLabel={`Start date: ${formatDisplayDateLong(startDate)}`}
              accessibilityRole="button"
              testID="goal-create.start-date-selector"
            >
              <Text style={styles.dateInputText} accessible={false}>{formatDisplayDateLong(startDate)}</Text>
            </TouchableOpacity>
          </View>
          <View style={styles.dateField}>
            <Text style={styles.dateLabel}>End Date</Text>
            <TouchableOpacity
              style={styles.dateInput}
              onPress={() => setShowEndPicker(true)}
              accessibilityLabel={`End date: ${formatDisplayDateLong(endDate)}`}
              accessibilityRole="button"
              testID="goal-create.end-date-selector"
            >
              <Text style={styles.dateInputText} accessible={false}>{formatDisplayDateLong(endDate)}</Text>
            </TouchableOpacity>
          </View>
        </View>

        <DatePickerModal
          visible={showStartPicker}
          value={startDate}
          onSelect={(d) => { setStartDate(d); setShowStartPicker(false); }}
          onClose={() => setShowStartPicker(false)}
          title="Start Date"
        />
        <DatePickerModal
          visible={showEndPicker}
          value={endDate}
          onSelect={(d) => { setEndDate(d); setShowEndPicker(false); }}
          onClose={() => setShowEndPicker(false)}
          title="End Date"
        />

        {/* Measures & Milestones */}
        <Text style={styles.label}>MEASURES & MILESTONES</Text>

        {measures.length > 0 && (
          <View style={styles.measuresContainer}>
            {measures.map((measure, idx) => (
              <View key={measure.id} style={styles.measureRow}>
                <View style={styles.measureHeader}>
                  <Text style={styles.measureLabel}>Measure {idx + 1}</Text>
                  <TouchableOpacity
                    onPress={() => removeMeasure(measure.id)}
                    accessibilityLabel={`Remove measure ${idx + 1}`}
                    testID={`goal-create.remove-measure-${idx}`}
                  >
                    <Text style={styles.removeText}>Remove</Text>
                  </TouchableOpacity>
                </View>
                <View style={styles.measureFields}>
                  <TextInput
                    style={[styles.measureInput, styles.measureInputName]}
                    value={measure.name}
                    onChangeText={(v) => updateMeasure(measure.id, 'name', v)}
                    placeholder="Name"
                    placeholderTextColor={colors.placeholder}
                    testID={`goal-create.measure-name-${idx}`}
                  />
                  <TextInput
                    style={[styles.measureInput, styles.measureInputUnit]}
                    value={measure.unit}
                    onChangeText={(v) => updateMeasure(measure.id, 'unit', v)}
                    placeholder="Unit"
                    placeholderTextColor={colors.placeholder}
                    testID={`goal-create.measure-unit-${idx}`}
                  />
                </View>
                <View style={styles.measureFields}>
                  <TextInput
                    style={[styles.measureInput, { flex: 1 }]}
                    value={measure.start_value}
                    onChangeText={(v) => updateMeasure(measure.id, 'start_value', v)}
                    placeholder="Start value"
                    placeholderTextColor={colors.placeholder}
                    keyboardType="numeric"
                    testID={`goal-create.measure-start-${idx}`}
                  />
                  <TextInput
                    style={[styles.measureInput, { flex: 1 }]}
                    value={measure.target_value}
                    onChangeText={(v) => updateMeasure(measure.id, 'target_value', v)}
                    placeholder="Target value"
                    placeholderTextColor={colors.placeholder}
                    keyboardType="numeric"
                    testID={`goal-create.measure-target-${idx}`}
                  />
                </View>
              </View>
            ))}

            {/* Milestone count */}
            <Text style={[styles.measureLabel, { marginTop: 4, marginBottom: 8 }]}>Milestones</Text>
            <View style={styles.milestoneRow}>
              <TouchableOpacity
                style={styles.stepperBtn}
                onPress={() => setMilestoneCount((c) => Math.max(1, (c ?? 4) - 1))}
                accessibilityLabel="Decrease milestones"
                testID="goal-create.milestone-decrease"
              >
                <Text style={styles.stepperText}>-</Text>
              </TouchableOpacity>
              <Text style={styles.milestoneValue} testID="goal-create.milestone-count">
                {milestoneCount ?? 4}
              </Text>
              <TouchableOpacity
                style={styles.stepperBtn}
                onPress={() => setMilestoneCount((c) => Math.min(12, (c ?? 4) + 1))}
                accessibilityLabel="Increase milestones"
                testID="goal-create.milestone-increase"
              >
                <Text style={styles.stepperText}>+</Text>
              </TouchableOpacity>
              <Text style={styles.milestoneHint}>checkpoints to track progress</Text>
            </View>
          </View>
        )}

        <TouchableOpacity
          style={styles.addMeasureBtn}
          onPress={addMeasure}
          accessibilityLabel="Add a measure"
          testID="goal-create.add-measure-button"
        >
          <Text style={styles.addMeasureText}>+ Add Measure</Text>
        </TouchableOpacity>

        {/* Description */}
        <Text style={styles.label}>DESCRIPTION</Text>
        <TextInput
          style={styles.textArea}
          value={description}
          onChangeText={setDescription}
          placeholder="Describe your goal using SMART criteria..."
          placeholderTextColor={colors.placeholder}
          multiline
          textAlignVertical="top"
          accessibilityLabel="Goal description"
          testID="goal-create.description-input"
        />

        <View style={styles.tipBox}>
          <Text style={styles.tipTitle}>{'\u{1F4A1}'} SMART Goals</Text>
          <Text style={styles.tipText}>
            Make your goal Specific, Measurable, Achievable, Relevant, and Time-bound for best results.
          </Text>
        </View>

        {/* AI Suggest Button */}
        {llm.available === false && (
          <AiUnavailableBanner reason={llm.unavailableReason} />
        )}
        {llm.available === true && (
          <TouchableOpacity
            style={styles.aiButton}
            onPress={handleAiSuggest}
            disabled={llm.loading}
            accessibilityLabel="Get AI suggestions"
            accessibilityRole="button"
            testID="goal-create.ai-suggest-button"
          >
            <Text style={styles.aiButtonText} accessible={false}>{'\u{1F9E0}'} AI Suggest</Text>
          </TouchableOpacity>
        )}

        {showAiResults && (
          <AiSuggestionsCard
            loading={llm.loading}
            modelDownloading={llm.modelDownloading}
            suggestions={aiSuggestions}
            error={llm.error}
            onApplySuggestion={(text) => {
              setDescription(text);
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
            onDismiss={() => {
              setShowAiResults(false);
              setAiSuggestions([]);
            }}
          />
        )}
      </ScrollView>
    </KeyboardAwareView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1, backgroundColor: theme.colors.bgSecondary },
  content: { flex: 1 },
  contentInner: { padding: 16, paddingBottom: 40 },
  label: { fontSize: 12, fontWeight: '700' as const, color: theme.colors.textMuted, letterSpacing: 0.5, marginBottom: 8, marginTop: 20 },
  input: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 16, color: theme.colors.textPrimary },
  selector: { flexDirection: 'row' as const, alignItems: 'center' as const, justifyContent: 'space-between' as const, backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14 },
  selectorText: { fontSize: 15, color: theme.colors.textSecondary, flex: 1 },
  chevron: { fontSize: 12, color: theme.colors.textDisabled, marginLeft: 8 },
  pickerList: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, marginTop: 4, overflow: 'hidden' as const },
  pickerItem: { paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: theme.colors.borderLight },
  pickerItemActive: { backgroundColor: theme.colors.primaryBg },
  pickerItemText: { fontSize: 14, color: theme.colors.textSecondary },
  dateRow: { flexDirection: 'row' as const, gap: 12 },
  dateField: { flex: 1 },
  dateLabel: { fontSize: 13, fontWeight: '500' as const, color: theme.colors.textSecondary, marginBottom: 6 },
  dateInput: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 12 },
  dateInputText: { fontSize: 14, color: theme.colors.textPrimary, fontWeight: '500' as const },
  textArea: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 14, fontSize: 15, color: theme.colors.textPrimary, minHeight: 120 },
  tipBox: { backgroundColor: theme.colors.primaryBg, borderRadius: 10, padding: 14, marginTop: 16 },
  tipTitle: { fontSize: 14, fontWeight: '700' as const, color: theme.colors.primaryDark, marginBottom: 4 },
  tipText: { fontSize: 13, color: theme.colors.primary, lineHeight: 18 },
  aiButton: { backgroundColor: theme.colors.aiBg, borderWidth: 1, borderColor: theme.colors.aiBorder, borderRadius: 10, paddingVertical: 12, alignItems: 'center' as const, marginTop: 16 },
  aiButtonText: { fontSize: 15, fontWeight: '600' as const, color: theme.colors.ai },
  measuresContainer: { backgroundColor: theme.colors.surface, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 10, padding: 12 },
  measureRow: { marginBottom: 12 },
  measureHeader: { flexDirection: 'row' as const, justifyContent: 'space-between' as const, alignItems: 'center' as const, marginBottom: 8 },
  measureLabel: { fontSize: 13, fontWeight: '600' as const, color: theme.colors.textSecondary },
  removeText: { fontSize: 13, color: theme.colors.danger, fontWeight: '500' as const },
  measureFields: { flexDirection: 'row' as const, gap: 8, marginBottom: 8 },
  measureInput: { backgroundColor: theme.colors.background, borderWidth: 1, borderColor: theme.colors.border, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: theme.colors.textPrimary },
  measureInputName: { flex: 2 },
  measureInputUnit: { flex: 1 },
  addMeasureBtn: { alignItems: 'center' as const, paddingVertical: 12, borderWidth: 1, borderColor: theme.colors.primary, borderRadius: 10, borderStyle: 'dashed' as const, marginTop: 8 },
  addMeasureText: { fontSize: 14, color: theme.colors.primary, fontWeight: '500' as const },
  milestoneRow: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 12 },
  stepperBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: theme.colors.primaryBg, alignItems: 'center' as const, justifyContent: 'center' as const },
  stepperText: { fontSize: 20, fontWeight: '600' as const, color: theme.colors.primary },
  milestoneValue: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textPrimary, minWidth: 30, textAlign: 'center' as const },
  milestoneHint: { fontSize: 13, color: theme.colors.textMuted, flex: 1 },
});
